import React, { useEffect, useRef, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Input, Textarea } from '../form/input';
import styles from './styles.module.scss';
import { useClickOutside, useToast } from 'hooks';
import { useMultipleChoiceQuestionPost, MultipleChoiceQuestionPayload } from 'hooks';
import { MagicPen, RemoveIcon, UploadIconThree } from 'assets';
import { useRefineText } from 'hooks/useRefineText';
import RefineModal from 'components/modal-variants/refine-modal';

// Mock icons - replace with your actual icons
const AddIcon = ({ className }: { className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const CircularPlusButton = ({ onClick, className = "" }) => (
  <span
    onClick={onClick}
    className={`${styles.circularButton} ${styles.plus} ${className}`}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M12 6V18M6 12H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);

export const CircularMinusButton = ({ onClick, className = "" }) => (
  <span
    onClick={onClick}
    className={`${styles.circularButton} ${styles.minus} ${className}`}
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path d="M6 12H18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);


interface OptionData {
  id: string;
  content: string;
  correct: boolean;
  xbox?: boolean;
}

interface QuestionData {
  id: string;
  question_content: string;
  description?: string;
  image?: string | null | undefined;
  image_description?: string;
  required: boolean;
  points_obtainable: number;
  maximum_selections: number;
  options: OptionData[];
}

interface QuestionFormData {
  questions: QuestionData[];
}

interface MultipleChoiceFormProps {
  assessmentId: string | null;
  submitQuestion: (options?: { skipped?: boolean }) => void;
  setImageUploadScroll: (value: boolean) => void;
  questionsData?: any;
  isSubmitted: boolean;
  creditsAvailable?: number;
  onInsufficientCredits?: (creditsRequired: number, creditsAvailable: number) => void;
}

// Yup validation schema
const optionSchema = yup.object().shape({
  id: yup.string().required(),
  content: yup.string().required('Option content is required'),
  correct: yup.boolean().default(false),
});

const questionSchema = yup.object().shape({
  id: yup.string().required(),
  question_content: yup.string().required('Question is required'),
  description: yup.string().optional(),
  image: yup.string().nullable(),
  image_description: yup.string().optional(),
  required: yup.boolean().default(false),
  points_obtainable: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .min(1, 'Points must be at least 1')
    .required('Please enter points for this question'),
  maximum_selections: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .min(1, 'Maximum selections must be at least 1')
    .required('Please enter maximum selections allowed'),
  options: yup
    .array()
    .of(optionSchema)
    .min(2, 'At least 2 options are required')
    .test('at-least-one-correct', 'Please select at least one correct answer', function (options) {
      return options && options.some(option => option.correct);
    })
    .required('Options are required'),
});

const questionFormSchema = yup.object().shape({
  questions: yup
    .array()
    .of(questionSchema)
    .min(1, 'At least one question is required')
    .required('Questions array is required'),
});

const buildDefaultQuestion = (): QuestionData => ({
  id: '1',
  question_content: '',
  description: '',
  image: null,
  image_description: '',
  required: false,
  points_obtainable: 5,
  maximum_selections: 4,
  options: [
    { id: '1', content: '', correct: false, xbox: false },
    { id: '2', content: '', correct: false, xbox: false },
    { id: '3', content: '', correct: false, xbox: false },
    { id: '4', content: '', correct: false, xbox: false },
  ],
});

const normalizeMultipleChoiceQuestion = (questionData: any, index: number): QuestionData => ({
  id: (index + 1).toString(),
  question_content: questionData?.question_content || questionData?.question || '',
  description: questionData?.description || '',
  image: questionData?.image || null,
  image_description: questionData?.image_description || '',
  required: questionData?.required || false,
  points_obtainable: questionData?.points_obtainable || 5,
  maximum_selections: questionData?.maximum_selections || 4,
  options: (questionData?.options || []).map((option: any, optIndex: number) => ({
    id: `${optIndex + 1}`,
    content: option.content || '',
    correct: option.correct || false,
    xbox: !(option.correct || false),
  })),
});

// Helper function to transform questionsData to form format
const transformQuestionsData = (questionsData: any): QuestionData[] => {
  if (!questionsData) {
    return [buildDefaultQuestion()];
  }

  // Handle array, { questions: [...] }, and { question1, question2, question3 } shapes —
  // user submissions arrive in any of these depending on whether the entry came from a
  // direct POST mirror or from the conversation API.
  const sourceQuestions: any[] = Array.isArray(questionsData)
    ? questionsData
    : Array.isArray(questionsData.questions)
      ? questionsData.questions
      : questionsData.questions && typeof questionsData.questions === 'object'
        ? Object.values(questionsData.questions)
      : ['question1', 'question2', 'question3']
          .map((key) => questionsData[key])
          .filter((q) => q && typeof q === 'object');

  if (sourceQuestions.length === 0) {
    return [buildDefaultQuestion()];
  }

  return sourceQuestions
    .slice(0, 3)
    .map((questionData, index) => normalizeMultipleChoiceQuestion(questionData, index));
};

const MultipleChoiceForm: React.FC<MultipleChoiceFormProps> = ({ assessmentId, submitQuestion, setImageUploadScroll, questionsData, isSubmitted, creditsAvailable = 0, onInsufficientCredits }) => {
  const [uploadedImages, setUploadedImages] = useState<{ [key: string]: { base64: string; fileName: string } }>({});
  const [previousImageCount, setPreviousImageCount] = useState(0);
  const { openToast } = useToast();
  const { mutate: submitMultipleChoiceQuestion, isPending: loading } = useMultipleChoiceQuestionPost();
  const defaultQuestions = transformQuestionsData(questionsData);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalText, setModalText] = useState('');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [refinedText, setRefinedText] = useState('');
  const [showPromptInput, setShowPromptInput] = useState(false);
  const textareaRef = useRef<any>(null);
  const { refineText, isLoading } = useRefineText({
    onSuccess: (refinedText) => {
      setRefinedText(refinedText);
      setModalText(refinedText);
      setShowPromptInput(true);
    },
  });
  const modalRef = useRef(null);
  useClickOutside(modalRef, () => setModalOpen(false));

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<QuestionFormData>({
    resolver: yupResolver(questionFormSchema),
    defaultValues: {
      questions: defaultQuestions,
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'questions',
  });

  // Reset form when questionsData changes
  useEffect(() => {
    const newDefaultQuestions = transformQuestionsData(questionsData);
    reset({
      questions: newDefaultQuestions,
    });
    replace(newDefaultQuestions);

    // Set up uploaded images from existing data
    const newUploadedImages: { [key: string]: { base64: string; fileName: string } } = {};
    newDefaultQuestions.forEach((question) => {
      if (question.image) {
        newUploadedImages[question.id] = {
          base64: question.image,
          fileName: 'Uploaded Image', // Default name since we don't have the original filename
        };
      }
    });
    setUploadedImages(newUploadedImages);
  }, [questionsData, replace, reset]);

  // Handle image upload and convert to base64
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>, questionId: string) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        setUploadedImages(prev => ({
          ...prev,
          [questionId]: { base64, fileName: file.name },
        }));

        const questionIndex = fields.findIndex(field => field.id === questionId);
        if (questionIndex !== -1) {
          setValue(`questions.${questionIndex}.image`, base64);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  // Add new question
  const addQuestion = () => {
    if (fields.length < 4) {
      append({
        id: Date.now().toString(),
        question_content: '',
        description: '',
        image: null,
        image_description: '',
        required: false,
        points_obtainable: 5,
        maximum_selections: 4,
        options: [
          { id: Date.now().toString() + '-1', content: '', correct: false, xbox: false },
          { id: Date.now().toString() + '-2', content: '', correct: false, xbox: false },
          { id: Date.now().toString() + '-3', content: '', correct: false, xbox: false },
          { id: Date.now().toString() + '-4', content: '', correct: false, xbox: false },
        ],
      });
    }
  };

  // Remove question
  const removeQuestion = (index: number) => {
    if (fields.length > 1) {
      const questionId = fields[index].id;
      remove(index);
      setUploadedImages(prev => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  // Remove option from question
  const removeOption = (questionIndex: number, optionIndex: number) => {
    const currentOptions = watch(`questions.${questionIndex}.options`);
    if (currentOptions.length > 2) {
      const updatedOptions = currentOptions.filter((_, index) => index !== optionIndex);
      setValue(`questions.${questionIndex}.options`, updatedOptions);
    }
  };

  // Toggle correct answer
  const toggleCorrectAnswer = (questionIndex: number, optionIndex: number) => {
    const currentOptions = watch(`questions.${questionIndex}.options`);
    const maxSelections = watch(`questions.${questionIndex}.maximum_selections`);

    if (maxSelections === 1) {
      // Single selection - uncheck all others
      const updatedOptions = currentOptions.map((option, index) => ({
        ...option,
        correct: index === optionIndex ? !option.correct : false,
        xbox: index === optionIndex ? false : option.xbox, // Clear xbox when correct is selected
      }));
      setValue(`questions.${questionIndex}.options`, updatedOptions);
    } else {
      // Multiple selection - toggle current
      const updatedOptions = [...currentOptions];
      updatedOptions[optionIndex] = {
        ...updatedOptions[optionIndex],
        correct: !updatedOptions[optionIndex].correct,
        xbox: false, // Clear xbox when correct is toggled
      };
      setValue(`questions.${questionIndex}.options`, updatedOptions);
    }
  };

  // Toggle xbox answer
  const toggleXboxAnswer = (questionIndex: number, optionIndex: number) => {
    const currentOptions = watch(`questions.${questionIndex}.options`);
    const maxSelections = watch(`questions.${questionIndex}.maximum_selections`);

    if (maxSelections === 1) {
      const updatedOptions = currentOptions.map((option, index) => ({
        ...option,
        xbox: index === optionIndex ? !option.xbox : false,
        correct: index === optionIndex ? false : option.correct, // Clear correct when xbox is selected
      }));
      setValue(`questions.${questionIndex}.options`, updatedOptions);
    } else {
      const updatedOptions = [...currentOptions];
      updatedOptions[optionIndex] = {
        ...updatedOptions[optionIndex],
        xbox: !updatedOptions[optionIndex].xbox,
        correct: false, // Clear correct when xbox is selected
      };
      setValue(`questions.${questionIndex}.options`, updatedOptions);
    }
  };

  // Update image scroll behavior
  useEffect(() => {
    const currentImageCount = Object.keys(uploadedImages).length;
    if (currentImageCount > previousImageCount) {
      setImageUploadScroll(true);
    }
    setPreviousImageCount(currentImageCount);
  }, [uploadedImages, previousImageCount, setImageUploadScroll]);

  // Form submission
  const onSubmit = async (data: QuestionFormData) => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: "Required",
        text: "Assessment ID is required",
        type: "error",
        timeOut: 5000,
      });
      return;
    }
    // Transform form data to API payload
    const payload: MultipleChoiceQuestionPayload = {
      assessment_id: assessmentId,
      question1: null,
      question2: null,
      question3: null,
    };

    data.questions.slice(0, 3).forEach((question, index) => {
      payload[`question${index + 1}`] = {
        question_content: question.question_content,
        image: question.image || null,
        image_description: question.image_description || '',
        description: question.description || '',
        question_type: 'multiple_choice',
        points_obtainable: question.points_obtainable,
        required: question.required,
        maximum_selections: question.maximum_selections,
        options: question.options.map(option => ({
          content: option.content,
          correct: option.correct,
        })),
      };
    });

    submitMultipleChoiceQuestion(payload, {
      onSuccess: () => {
        submitQuestion();
        openToast({
          show: true,
          heading: "Success",
          text: "Multiple choice question(s) created successfully!",
          type: "info",
          timeOut: 5000,
        });
      },
      onError: (err: any) => {
        console.error('Unexpected error during submission:', err);
        openToast({
          show: true,
          heading: 'Submission Failed',
          text: err?.response?.data?.message || 'An unexpected error occurred. Please try again.',
          type: 'error',
          timeOut: 5000,
        });
      },
    });
  };


  const handleMagicPenClick = (index: number, question: string) => {
    setQuestionIndex(index);
    setModalText(question);
    setModalOpen(true);
  };

  const handleClose = () => {
    setModalOpen(false);
    setRefinedText('');
    setShowPromptInput(false);
    setModalText(watch(`questions.${questionIndex}.question_content`))
  };

  const handleRefine = (prompt: string) => {
    if (!modalText) return;
    const creditsNeeded = 3;
    if (creditsAvailable < creditsNeeded) {
      onInsufficientCredits?.(creditsNeeded, creditsAvailable);
      return;
    }
    refineText({
      question: modalText,
      prompt: prompt,
    });
  };

  const handleInsert = () => {
    setValue(`questions.${questionIndex}.question_content`, refinedText);
    if (textareaRef.current) {
      textareaRef.current.value = refinedText;
    }
    setModalOpen(false);
    setShowPromptInput(false);
    setRefinedText('');
  };

  return (
    <div className={styles.questionForm}>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        {/* Questions Section */}
        <div className={styles.questionsSection}>
          {fields.map((field, questionIndex) => {
            const watchRequired = watch(`questions.${questionIndex}.required`);
            const questionOptions = watch(`questions.${questionIndex}.options`) || [];
            const questionContent = watch(`questions.${questionIndex}.question_content`);
            const hasQuestionContent = questionContent && questionContent.trim().length > 0;

            return (
              <div key={field.id} className={styles.questionItem}>
                {(uploadedImages[field.id] || watch(`questions.${questionIndex}.image`)) && (
                  <div className={styles.imagePreview}>
                    <img
                      src={
                        uploadedImages[field.id]?.base64 ||
                        (watch(`questions.${questionIndex}.image`) ?? undefined)
                      }
                      alt="Preview"
                      className={styles.previewImage}
                    />
                  </div>
                )}

                <div className={styles.questionHeader}>
                  {fields.length > 1 && <span className={styles.questionNumber}>Question {questionIndex + 1}</span>}
                  {fields.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(questionIndex)}
                      className={styles.removeButton}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Question Input */}
                <div className={styles.questionSection}>
                  <div className={styles.questionInputContainer}>
                    <Textarea
                      name={`questions.${questionIndex}.question_content`}
                      placeholder="Type your question here"
                      register={register}
                      value={watch(`questions.${questionIndex}.question_content`) || ''}
                      validatorMessage={errors.questions?.[questionIndex]?.question_content?.message}
                      className={styles.questionInput}
                      parentClassName={styles.questionInputWrapper}
                      required
                    />
                    <MagicPen className={styles.magicPen} onClick={() => handleMagicPenClick(questionIndex, watch(`questions.${questionIndex}.question_content`))} />
                  </div>

                  <div className={styles.questionControlSection}>
                    <CircularPlusButton
                      onClick={addQuestion}
                      className={styles.buttons}
                    />

                    {fields.length > 1 && (
                      <CircularMinusButton
                        onClick={() => removeQuestion(questionIndex)}
                        className={styles.buttons}
                      />
                    )}
                  </div>
                </div>

                {/* Options Section */}
                <div className={styles.optionsSection}>
                  {questionOptions.map((option, optionIndex) => (
                    <div key={option.id || optionIndex} className={styles.optionItem}>
                      <div className={styles.optionContent}>
                        <Input
                          name={`questions.${questionIndex}.options.${optionIndex}.content`}
                          placeholder={`Input Option ${optionIndex + 1}`}
                          register={register}
                          validatorMessage={errors.questions?.[questionIndex]?.options?.[optionIndex]?.content?.message}
                          className={`${styles.optionInput} ${option.correct ? styles.correct : ''}`}
                          parentClassName={styles.optionInputWrapper}
                          required
                          disabled={!hasQuestionContent}
                        />

                        <div className={styles.correctAnswerToggle}>
                          <input
                            type="checkbox"
                            checked={option.correct}
                            onChange={() => toggleCorrectAnswer(questionIndex, optionIndex)}
                            className={styles.correctInput}
                            disabled={!hasQuestionContent}
                          />
                        </div>

                        <div className={styles.wrongAnswerToggle}>
                          <input
                            type="checkbox"
                            checked={option.xbox}
                            onChange={() => toggleXboxAnswer(questionIndex, optionIndex)}
                            className={styles.correctInput}
                            disabled={!hasQuestionContent}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Form Controls */}
                <div className={styles.controlsSection}>
                  <div className={styles.controls}>
                    {/* Upload Image */}
                    {(uploadedImages[field.id] || watch(`questions.${questionIndex}.image`)) ? (
                      <div className={styles.imageUpload}>
                        <div className={styles.fileNameContainer}>
                          <span className={styles.fileName}>
                            {uploadedImages[field.id]?.fileName || 'Uploaded Image'}
                          </span>
                          <span
                            onClick={() => {
                              setUploadedImages(prev => {
                                const updated = { ...prev };
                                delete updated[field.id];
                                return updated;
                              });
                              setValue(`questions.${questionIndex}.image`, null);
                            }}
                            className={styles.removeImageButton}
                          >
                            <RemoveIcon className={styles.removeIcon} />
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.imageUpload}>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, field.id)}
                          className={styles.fileInput}
                          id={`image-${field.id}`}
                          disabled={!hasQuestionContent}
                        />
                        <label htmlFor={`image-${field.id}`} className={styles.uploadButton}>
                          Upload image
                          <UploadIconThree className={styles.uploadIcon} />
                        </label>
                      </div>
                    )}

                    {/* Required Toggle */}
                    <div className={styles.toggleGroup}>
                      <label className={styles.toggleLabel}>
                        Required
                        <input
                          type="checkbox"
                          {...register(`questions.${questionIndex}.required`)}
                          className={styles.toggleInput}
                        />
                        <span className={`${styles.toggleSlider} ${watchRequired ? styles.active : ''}`}>
                          <span className={styles.toggleButton}></span>
                        </span>
                      </label>
                    </div>

                    {/* Maximum Selections */}
                    <div className={styles.inputGroup}>
                      <label className={styles.inputLabel}>Max selections</label>
                      <Input
                        name={`questions.${questionIndex}.maximum_selections`}
                        type="number"
                        placeholder="1"
                        register={register}
                          value={watch(`questions.${questionIndex}.maximum_selections`)?.toString() || ''}
                        validatorMessage={errors.questions?.[questionIndex]?.maximum_selections?.message}
                        className={styles.numberInput}
                        parentClassName={styles.numberInputContainer}
                        min={1}
                        max={questionOptions.length}
                        required
                        disabled={!hasQuestionContent}
                      />
                    </div>

                    {/* Points */}
                    <div className={styles.inputGroup}>
                      <label className={styles.inputLabel}>Points</label>
                      <Input
                        name={`questions.${questionIndex}.points_obtainable`}
                        type="number"
                        placeholder="5"
                        register={register}
                          value={watch(`questions.${questionIndex}.points_obtainable`)?.toString() || ''}
                        validatorMessage={errors.questions?.[questionIndex]?.points_obtainable?.message}
                        className={styles.numberInput}
                        parentClassName={styles.numberInputContainer}
                        min={1}
                        required
                        disabled={!hasQuestionContent}
                      />
                    </div>


                  </div>
                </div>

              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        {!isSubmitted && (<div className={styles.actionButtons}>
          <button
            type="button"
            onClick={addQuestion}
            className={styles.addButton}
          >
            <AddIcon className={styles.addIcon} />
            Add
          </button>

          <button
            type="button"
            onClick={() => submitQuestion({ skipped: true })}
            className={styles.skipButton}
            disabled={loading}
          >
            Skip
          </button>

          <button
            type="submit"
            className={styles.submitButton}
            disabled={loading}
            onClick={() => {

              const currentQuestions = watch('questions');

              // Validate that each question has options
              const questionsWithoutOptions = currentQuestions.filter(question =>
                !question.options || question.options.length === 0
              );


              if (questionsWithoutOptions.length > 0) {
                openToast({
                  show: true,
                  heading: "Validation Error",
                  text: "Please add at least 2 options for each question",
                  type: "error",
                  timeOut: 5000,
                });
                return;
              }

              // Validate that each question has at least one correct option
              const questionsWithoutCorrectOption = currentQuestions.filter(question =>
                !question.options.some(option => option.correct)
              );

              if (questionsWithoutCorrectOption.length > 0) {
                openToast({
                  show: true,
                  heading: "Validation Error",
                  text: "Please select at least one correct option for each question",
                  type: "error",
                  timeOut: 5000,
                });
                return;
              }
            }}
          >
            {loading ? 'Submitting...' : 'Done'}
          </button>
        </div>)}
      </form>

      <RefineModal
        isOpen={modalOpen}
        onClose={handleClose}
        modalText={modalText}
        refinedText={refinedText}
        onRefine={handleRefine}
        onInsert={handleInsert}
        isLoading={isLoading}
        showInput={showPromptInput}
      />
    </div>
  );
};

export { MultipleChoiceForm };